import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateTime, localDateOnlyValue } from "../dates";
import { alphaMutationRejected } from "../alpha-mutation";
import { useStaffSession } from "../ui/StaffSession";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function PlanningAlpha() {
  const session = useStaffSession();
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<any>(),
    [candidates, setCandidates] = useState<any[]>([]),
    [staffDate, setStaffDate] = useState(localDateOnlyValue()),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/planning?building=" + building).then(setModel)
      : Promise.resolve();
  useEffect(() => {
    setModel(undefined);
    setCandidates([]);
    void load().catch(setError);
  }, [building]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const period = (
    <>
      <label>
        From
        <DateOnlyInput name="start" required />
      </label>
      <label>
        To
        <DateOnlyInput name="end" required />
      </label>
    </>
  );
  return (
    <>
      <h1>Management planning</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/api/alpha/planning/workload", {
                building,
                start: f.get("start"),
                end: f.get("end"),
                hourlyCost: f.get("cost"),
                note: f.get("note"),
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Workload and contribution estimate</legend>
              {period}
              <label>
                Assumed staff cost per hour, £
                <input name="cost" inputMode="decimal" required />
              </label>
              <label>
                Purpose and assumptions
                <textarea name="note" required minLength={5} />
              </label>
              <p>
                Uses current executed fees and recorded field effort. Unrecorded
                management time and overheads are excluded, so this is not total
                profit.
              </p>
              <button>Save workload scenario</button>
            </fieldset>
          </form>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/api/alpha/planning/benchmark", {
                buildings: [building, ...f.getAll("buildings")],
                start: f.get("start"),
                end: f.get("end"),
                note: f.get("note"),
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Compare similar buildings</legend>
              {period}
              <label>
                Other comparable buildings
                <select name="buildings" multiple required>
                  {session?.buildings
                    .filter((b) => b.id !== building)
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Why these buildings are comparable
                <textarea name="note" required minLength={10} />
              </label>
              <button>Save comparison</button>
            </fieldset>
          </form>
          <section className="panel">
            <h2>Staffing and coverage scenario</h2>
            <label>
              Date
              <DateOnlyInput
                value={staffDate}
                onChange={(value) => {
                  setStaffDate(value);
                  setCandidates([]);
                }}
              />
            </label>
            <button
              disabled={busy}
              onClick={() =>
                void api<any>(
                  "/api/alpha/planning/staff?building=" +
                    building +
                    "&date=" +
                    staffDate,
                )
                  .then((r) => setCandidates(r.candidates))
                  .catch(setError)
              }
            >
              Review availability
            </button>
            {candidates.length > 0 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void save("/api/alpha/planning/staff", {
                    building,
                    date: staffDate,
                    assignments: candidates
                      .filter((c) => Number(f.get(c.id)) > 0)
                      .map((c) => ({
                        userId: c.id,
                        minutes: Number(f.get(c.id)),
                      })),
                    note: f.get("note"),
                  });
                }}
              >
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Plan staff time</legend>
                  {candidates.map((c) => (
                    <label key={c.id}>
                      {c.display_name} · {c.assignment.availability.status} ·{" "}
                      {c.assignment.availability.usableMinutes ?? "Unknown"}{" "}
                      available minutes
                      <input
                        name={c.id}
                        type="number"
                        min={0}
                        max={1440}
                        defaultValue={0}
                      />
                    </label>
                  ))}
                  <label>
                    Purpose and responsibilities
                    <textarea name="note" required minLength={5} />
                  </label>
                  <button>Save staffing scenario</button>
                </fieldset>
              </form>
            )}
          </section>
          <h2>Retained scenarios</h2>
          {!model.reports.length&&<p className="empty">No planning scenarios have been saved for this building.</p>}
          {model.reports.map((r: any) => (
            <section className="panel" key={r.id}>
              <h3>
                {r.report_kind} · {formatDateTime(r.generated_at)}
              </h3>
              <p>{r.commentary}</p>
              <p>{r.snapshot.basis}</p>
              {r.snapshot.coverage && <p>{r.snapshot.coverage}</p>}
              {r.report_kind === "Workload scenario" && (
                <dl>
                  <dt>Recorded field effort</dt>
                  <dd>
                    {r.snapshot.recorded_minutes} minutes across{" "}
                    {r.snapshot.effort_records} records
                  </dd>
                  <dt>Reports without effort</dt>
                  <dd>{r.snapshot.reports_without_effort}</dd>
                  <dt>Estimated recorded-effort cost</dt>
                  <dd>£{r.snapshot.recordedEffortCost}</dd>
                  <dt>Illustrative fee for period</dt>
                  <dd>
                    {r.snapshot.illustrativePeriodFee === null
                      ? "Unknown or conflicting agreement"
                      : `£${r.snapshot.illustrativePeriodFee}`}
                  </dd>
                  <dt>Illustrative contribution before omitted costs</dt>
                  <dd>
                    {r.snapshot.illustrativeContribution === null
                      ? "Unknown"
                      : `£${r.snapshot.illustrativeContribution}`}
                  </dd>
                </dl>
              )}
              {r.snapshot.rows && (
                <table>
                  <thead>
                    <tr>
                      <th>Building</th>
                      <th>Units</th>
                      <th>Posted expenditure</th>
                      <th>Per unit</th>
                      <th>Effort minutes</th>
                      <th>Reports without effort</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.snapshot.rows.map((b: any) => (
                      <tr key={b.id}>
                        <td>{b.name}</td>
                        <td>{b.units}</td>
                        <td>{b.posted_expenditure ?? "Unknown"}</td>
                        <td>{b.expenditurePerUnit ?? "Unknown"}</td>
                        <td>{b.recorded_minutes}</td>
                        <td>{b.reports_without_effort}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {r.snapshot.assignments?.map((a: any) => (
                <p key={a.userId}>
                  {a.name}: {a.plannedMinutes} minutes planned ·{" "}
                  {a.availability.status} ·{" "}
                  {a.uncoveredMinutes === null
                    ? "Capacity unknown"
                    : `${a.uncoveredMinutes} minutes uncovered`}
                  {a.reviewRequired ? " · Review required" : ""}
                </p>
              ))}
            </section>
          ))}
        </>
      )}
    </>
  );
}
